import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal } from 'src/components/Modal/Modal';
import { convertDate, formatFieldString, shortenString } from 'src/components/Table/helpers';
import { formatCaseUserName } from 'src/api/adminCompliance';
import { ROUTE_USERS } from 'src/utils/routes';

import {
  Breadcrumb,
  Card,
  CardTitle,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  HeadlineRow,
  MetaLine,
  StatusBadge,
  Title,
  TitleGroup,
  UserLink,
} from 'src/pages/Transactions/styled';
import CopyableValue from 'src/pages/Transactions/CopyableValue';

type BankAccountDetailModalProps = {
  account: Record<string, any>;
  onClose: () => void;
};

const SectionSpacer = () => <div style={{ height: '1rem' }} />;

const pick = (...values: unknown[]) =>
  values.find((v) => v !== undefined && v !== null && v !== '') as string | number | undefined;


const BankAccountDetailModal: React.FC<BankAccountDetailModalProps> = ({ account, onClose }) => {
  const navigate = useNavigate();

  const owner = account.owner;
  const ownerId = pick(owner?.id, owner?._id) as string | undefined;
  const ownerName = formatCaseUserName(
    owner
      ? {
          name: owner.name,
          email: owner.email,
        }
      : null,
  );
  const ownerEmail = owner?.email as string | undefined;

  const id = String(pick(account.id, account._id) ?? '');
  const status = account.status ? String(account.status) : '';
  const bankName = account.bank_name ? String(account.bank_name) : '';
  const accountNumber = account.account_number ? String(account.account_number) : '';
  const swift = account.swift_bic_code ? String(account.swift_bic_code) : '';
  const iban = account.iban ? String(account.iban) : '';
  const bankCountry =
    typeof account.bank_country === 'object'
      ? account.bank_country?.name || account.bank_country?.code
      : account.bank_country;
  const createdAt = account.created_at as string | undefined;
  const updatedAt = account.updated_at as string | undefined;

  const headline = [bankName || null, accountNumber || null].filter(Boolean).join(' · ') || 'Bank account';
  const createdMeta = createdAt
    ? `Created ${convertDate(createdAt).date}${
        convertDate(createdAt).time ? ` · ${convertDate(createdAt).time}` : ''
      }`
    : null;

  return (
    <Modal isOpen onClose={onClose}>
      <Breadcrumb>Bank accounts{id ? ` / ${shortenString(id)}` : ''}</Breadcrumb>

      <HeadlineRow>
        <TitleGroup>
          <Title>
            {headline}
            {status ? (
              <StatusBadge $status={status}>{formatFieldString(status)}</StatusBadge>
            ) : null}
          </Title>
          {createdMeta ? <MetaLine>{createdMeta}</MetaLine> : null}
        </TitleGroup>
      </HeadlineRow>

      {(ownerName || ownerId || ownerEmail) && (
        <>
          <Card>
            <CardTitle>User</CardTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Name</DetailLabel>
                {ownerId ? (
                  <DetailValue title={ownerEmail ? `${ownerName} (${ownerEmail})` : ownerName}>
                    <UserLink
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate(`${ROUTE_USERS}/${ownerId}`);
                      }}
                    >
                      {ownerName && ownerName !== '—' ? ownerName : shortenString(ownerId)}
                    </UserLink>
                  </DetailValue>
                ) : (
                  <DetailValue>{ownerName || '—'}</DetailValue>
                )}
              </DetailItem>
              <DetailItem>
                <DetailLabel>Email</DetailLabel>
                <DetailValue $muted={!ownerEmail}>{ownerEmail || '—'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>BxLend ID</DetailLabel>
                <DetailValue $muted={!owner?.bxlend_id}>{owner?.bxlend_id || '—'}</DetailValue>
              </DetailItem>
            </DetailGrid>
          </Card>
          <SectionSpacer />
        </>
      )}

      <Card>
        <CardTitle>Bank account</CardTitle>
        <DetailGrid>
          {id ? (
            <DetailItem>
              <DetailLabel>Account ID</DetailLabel>
              <CopyableValue value={id} label="Bank account ID" />
            </DetailItem>
          ) : null}
          <DetailItem>
            <DetailLabel>Account number</DetailLabel>
            {accountNumber ? (
              <CopyableValue value={accountNumber} shorten={false} label="Account number" />
            ) : (
              <DetailValue $muted>—</DetailValue>
            )}
          </DetailItem>
          <DetailItem>
            <DetailLabel>Bank name</DetailLabel>
            <DetailValue $muted={!bankName}>{bankName || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>SWIFT / BIC</DetailLabel>
            <DetailValue $muted={!swift}>{swift || '—'}</DetailValue>
          </DetailItem>
          {iban ? (
            <DetailItem>
              <DetailLabel>IBAN</DetailLabel>
              <CopyableValue value={iban} shorten={false} label="IBAN" />
            </DetailItem>
          ) : null}
          {bankCountry ? (
            <DetailItem>
              <DetailLabel>Bank country</DetailLabel>
              <DetailValue>{String(bankCountry)}</DetailValue>
            </DetailItem>
          ) : null}
          <DetailItem>
            <DetailLabel>Status</DetailLabel>
            <DetailValue>
              {status ? (
                <StatusBadge $status={status}>{formatFieldString(status)}</StatusBadge>
              ) : (
                '—'
              )}
            </DetailValue>
          </DetailItem>
          {createdAt ? (
            <DetailItem>
              <DetailLabel>Created</DetailLabel>
              <DetailValue>
                {convertDate(createdAt).date}
                {convertDate(createdAt).time ? ` · ${convertDate(createdAt).time}` : ''}
              </DetailValue>
            </DetailItem>
          ) : null}
          {updatedAt ? (
            <DetailItem>
              <DetailLabel>Updated</DetailLabel>
              <DetailValue>
                {convertDate(updatedAt).date}
                {convertDate(updatedAt).time ? ` · ${convertDate(updatedAt).time}` : ''}
              </DetailValue>
            </DetailItem>
          ) : null}
        </DetailGrid>
      </Card>
    </Modal>
  );
};

export default BankAccountDetailModal;
